import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  HomeIcon,
  AcademicCapIcon,
  UsersIcon,
  UserGroupIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  PlusCircleIcon,
  ClipboardDocumentListIcon,
  DocumentTextIcon,
  DocumentDuplicateIcon,
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  BanknotesIcon,
  ReceiptPercentIcon,
  WindowIcon,
  MagnifyingGlassIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';

const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin, logout, loading } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  // 관리자가 아니면 관리자 로그인 페이지로 리다이렉트
  useEffect(() => {
    // 로딩 중이 아닐 때만 체크
    if (!loading && (!user || !isAdmin)) {
      navigate('/admin/login');
    }
  }, [user, isAdmin, navigate, loading]);

  const menuItems = [
    {
      title: '대시보드',
      path: '/admin',
      icon: HomeIcon
    },
    {
      title: '강의 관리',
      icon: AcademicCapIcon,
      subItems: [
        { title: '강의 목록', path: '/admin/courses', icon: ClipboardDocumentListIcon },
        { title: '강의 등록', path: '/admin/courses/new', icon: PlusCircleIcon }
      ]
    },
    {
      title: '반/모집 관리',
      path: '/admin/class-management',
      icon: CalendarDaysIcon
    },
    {
      title: '단체체험 관리',
      icon: UserGroupIcon,
      subItems: [
        { title: '예약 목록', path: '/admin/group-bookings', icon: ClipboardDocumentListIcon },
        { title: '날짜 관리', path: '/admin/group-dates', icon: CalendarDaysIcon }
      ]
    },
    {
      title: '센터 일정 관리',
      path: '/admin/center-schedule',
      icon: CalendarDaysIcon
    },
    {
      title: '게시글 관리',
      icon: DocumentTextIcon,
      subItems: [
        { title: '공지사항 목록', path: '/admin/notices', icon: ClipboardDocumentListIcon },
        { title: '공지사항 등록', path: '/admin/notices/new', icon: PlusCircleIcon },
        { title: '센터 NEWS 목록', path: '/admin/news', icon: ClipboardDocumentListIcon },
        { title: '센터 NEWS 등록', path: '/admin/news/new', icon: PlusCircleIcon },
        { title: '교육자료 목록', path: '/admin/materials', icon: ClipboardDocumentListIcon },
        { title: '교육자료 등록', path: '/admin/materials/new', icon: PlusCircleIcon },
        { title: '갤러리 목록', path: '/admin/gallery', icon: ClipboardDocumentListIcon },
        { title: '갤러리 등록', path: '/admin/gallery/new', icon: PlusCircleIcon },
        { title: 'FAQ 목록', path: '/admin/faqs', icon: ClipboardDocumentListIcon },
        { title: 'FAQ 등록', path: '/admin/faqs/new', icon: PlusCircleIcon },
        { title: '영상 갤러리 목록', path: '/admin/video-gallery', icon: VideoCameraIcon },
        { title: '영상 갤러리 등록', path: '/admin/video-gallery/new', icon: PlusCircleIcon },
        { title: '수강후기 목록', path: '/admin/reviews', icon: ClipboardDocumentListIcon },
        { title: '수강후기 등록', path: '/admin/reviews/new', icon: PlusCircleIcon },
        { title: '설문조사 목록', path: '/admin/surveys', icon: ClipboardDocumentListIcon },
        { title: '설문조사 등록', path: '/admin/surveys/new', icon: PlusCircleIcon }
      ]
    },
    {
      title: '페이지 관리',
      path: '/admin/page-contents',
      icon: DocumentDuplicateIcon
    },
    {
      title: '팝업 관리',
      path: '/admin/popups',
      icon: WindowIcon
    },
    {
      title: '수강생 관리',
      path: '/admin/students',
      icon: UsersIcon
    },
    {
      title: '수강생 이력',
      path: '/admin/student-history',
      icon: MagnifyingGlassIcon
    },
    {
      title: '회원 관리',
      path: '/admin/members',
      icon: UserGroupIcon
    },
    {
      title: '환불 관리',
      path: '/admin/refunds',
      icon: BanknotesIcon
    },
    {
      title: '감면 관리',
      path: '/admin/discounts',
      icon: ReceiptPercentIcon
    },
    {
      title: '통계',
      path: '/admin/statistics',
      icon: ChartBarIcon
    },
    {
      title: '설정',
      path: '/admin/settings',
      icon: Cog6ToothIcon
    }
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  // 로딩 중이면 로딩 화면 표시
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-indigo-600 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-500">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 bg-white rounded-lg border border-gray-200 transition-colors duration-150 hover:bg-gray-50"
        >
          {isSidebarOpen ? (
            <XMarkIcon className="h-5 w-5 text-gray-700" />
          ) : (
            <Bars3Icon className="h-5 w-5 text-gray-700" />
          )}
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-40 bg-white border-r border-gray-200 transform transition-all duration-200 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 ${isCollapsed ? 'w-16' : 'w-64'}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo & Toggle */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
            {!isCollapsed && <h1 className="text-base font-semibold text-gray-900">DGTC 관리자</h1>}
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:flex p-1.5 hover:bg-gray-100 rounded-lg transition-colors duration-150"
            >
              {isCollapsed ? (
                <ChevronRightIcon className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronLeftIcon className="h-4 w-4 text-gray-600" />
              )}
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            {menuItems.map((item, index) => (
              <div key={index} className="mb-1">
                {item.subItems ? (
                  <div>
                    <div className={`flex items-center px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${isCollapsed ? 'justify-center' : ''}`}>
                      <item.icon className={`h-4 w-4 ${!isCollapsed && 'mr-2'}`} />
                      {!isCollapsed && <span>{item.title}</span>}
                    </div>
                    {!isCollapsed && (
                      <div className="ml-6 space-y-1">
                        {item.subItems.map((subItem, subIndex) => (
                          <Link
                            key={subIndex}
                            to={subItem.path}
                            onClick={() => setIsSidebarOpen(false)}
                            className={`flex items-center px-3 py-2 text-sm rounded-lg transition-all duration-150 ${
                              isActivePath(subItem.path)
                                ? 'bg-indigo-50 text-indigo-600 font-medium'
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <subItem.icon className="h-4 w-4 mr-2" />
                            {subItem.title}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    to={item.path}
                    onClick={() => setIsSidebarOpen(false)}
                    className={`flex items-center px-3 py-2 rounded-lg transition-all duration-150 ${
                      isActivePath(item.path)
                        ? 'bg-indigo-50 text-indigo-600 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    } ${isCollapsed ? 'justify-center' : ''}`}
                    title={isCollapsed ? item.title : ''}
                  >
                    <item.icon className={`h-4 w-4 ${!isCollapsed && 'mr-3'}`} />
                    {!isCollapsed && <span className="text-sm">{item.title}</span>}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Logout */}
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className={`flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-all duration-150 ${isCollapsed ? 'justify-center' : ''}`}
              title={isCollapsed ? '로그아웃' : ''}
            >
              <ArrowLeftOnRectangleIcon className={`h-4 w-4 ${!isCollapsed && 'mr-3'}`} />
              {!isCollapsed && <span>로그아웃</span>}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`min-h-screen transition-all duration-200 ${isCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="bg-white border-b border-gray-200">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">
                동두천시 글로벌인재교육센터
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600 hidden sm:inline">{user?.name}님</span>
                <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-medium">
                    {user?.name?.charAt(0) || 'A'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black bg-opacity-30 transition-opacity duration-200"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;